import { Text, Heading, LiliButton, FontWeight } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import LiliLogo from '@Assets/logos/lili-logo-blue.svg';
import WelcomeRankingBanner from '@Assets/banners/welcome-ranking.svg';
import {
  AuthorizedUserOnboardingType,
  AuthorizedUserType,
} from '@Store/slices/authOnboarding/authOnboarding.types';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/useBoundStore';
import { RoutesMap } from '@Router/router.types';
import { identifyLogRocketUser } from '@Services/analytics/logrocket.service';
import { useStartAuthorizedUserOnboarding } from '@Pages/happyFlowPages/phone/api/phone.api';

export const InviteTeamMemberIntro = () => {
  const {
    customerFirstName: customerName,
    customerBusinessName: businessName,
    authorizedUserType: flow,
  } = useBoundStore.getState().authOnboardingSlice;
  const { t } = useTranslation('inviteTeamMember');
  const { navigateToNextPage } = useNavigationManager();
  const { authorizedUserId } = useBoundStore.getState().onboardingSlice;
  const { mutateAsync: startOnboarding, isPending } = useStartAuthorizedUserOnboarding();

  useEffect(() => {
    identifyLogRocketUser(authorizedUserId);
  }, [authorizedUserId]);

  const handleGetStarted = async () => {
    const onboardingType = useBoundStore.getState().getOnboardingType();
    if (onboardingType === AuthorizedUserOnboardingType.KYC_ONLY) {
      const response = await startOnboarding({});
      if (response.mfaDestination) {
        const customerDataToUpdate = useBoundStore.getState().getCustomerData();
        customerDataToUpdate.customerData.phoneNumber = response.mfaDestination;
        useBoundStore.getState().setCustomerData(customerDataToUpdate);
      }
    }
    navigateToNextPage(RoutesMap.INVITE_TEAM_MEMBER_INTRO);
  };

  const getDescriptionByFlow = () => {
    if (flow === AuthorizedUserType.ACCOUNTANT) {
      return t('inviteTeamMemberIntro.inviteMessage', { customerName, businessName });
    }
    if (flow === AuthorizedUserType.MANAGER) {
      return t('inviteTeamMemberIntro.inviteMessageManager', { customerName, businessName });
    }
    return t('inviteTeamMemberIntro.inviteMessageAdmin', { customerName, businessName });
  };

  return (
    <div className='flex flex-col items-center justify-center w-full'>
      <div className='flex flex-col items-center mt-[240px] min-h-screen md:max-w-[450px] max-w-[340px]'>
        <img
          src={LiliLogo}
          alt={t('inviteTeamMemberIntro.logoAlt')}
          className='w-[94px] justify-self-center mb-[24px]'
        />
        <div className='flex flex-col text-center w-full items-center gap-[52px]'>
          <div>
            <Heading.H3 fontWeight={FontWeight.SEMIBOLD} className='mb-[4px]'>
              {t('inviteTeamMemberIntro.welcomeHeader')}
            </Heading.H3>
            <Heading.H7>{t('inviteTeamMemberIntro.welcomeSubheader')}</Heading.H7>
          </div>

          <Text.H9 className='text-text-main-secondary'>{getDescriptionByFlow()}</Text.H9>

          <LiliButton
            id='get-started-button'
            title={t('inviteTeamMemberIntro.getStarted')}
            onClick={handleGetStarted}
            isLoading={isPending}
          />
        </div>
      </div>
      <div className='flex flex-row gap-4 items-center absolute bottom-[40px] left-1/2 -translate-x-1/2'>
        <img
          className='w-[320px]'
          src={WelcomeRankingBanner}
          alt={t('inviteTeamMemberIntro.trustPilotAlt')}
        />
      </div>
    </div>
  );
};

export default InviteTeamMemberIntro;
